import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter, 
  Button, 
  OutlineButton, 
  Badge, 
  FlexContainer, 
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableHeader
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { ProjectFinancialSummary, ProjectPayment, ProjectCashFlow } from '../../types';
import apiService from '../../services/api';
import CashFlowAnalysis from './CashFlowAnalysis';

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const StatusBadge = styled(Badge)`
  font-size: 0.9rem;
  padding: 0.35rem 0.75rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: var(--secondary-color);
  border-bottom: 2px solid var(--light-gray);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
`;

const DetailItem = styled.div`
  margin-bottom: 1rem;
`;

const DetailLabel = styled.div`
  font-weight: 600;
  color: var(--dark-gray);
  margin-bottom: 0.25rem;
`;

const DetailValue = styled.div`
  color: var(--text-color);
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryItem = styled.div`
  text-align: center;
  padding: 1rem;
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  
  .label {
    font-size: 0.9rem;
    color: var(--dark-gray);
    margin-bottom: 0.5rem;
  }
  
  .value {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--primary-color);
  }
  
  .positive {
    color: var(--success-color);
  }
  
  .negative {
    color: var(--danger-color);
  }
`;

const VarianceIndicator = styled.span<{ variance: number }>`
  color: ${props => props.variance >= 0 ? 'var(--success-color)' : 'var(--danger-color)'};
  font-weight: 600;
`;

const CashFlowChart = styled.div`
  background: white;
  border-radius: var(--border-radius);
  padding: 1rem;
  margin-bottom: 1rem;
  
  .chart-container {
    height: 200px;
    display: flex;
    align-items: end;
    gap: 2px;
    margin-top: 1rem;
  }
  
  .bar {
    flex: 1;
    background: var(--primary-color);
    border-radius: 2px 2px 0 0;
    position: relative;
  }
  
  .bar.income {
    background: var(--success-color);
  }
  
  .bar.expense {
    background: var(--danger-color);
  }
  
  .bar-label {
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.7rem;
    color: var(--dark-gray);
  }
`;

const CashFlowStatement = styled.div`
  background: white;
  border-radius: var(--border-radius);
  padding: 1rem;
  margin-bottom: 1rem;
  
  .statement-section {
    margin-bottom: 1.5rem;
    
    h4 {
      color: var(--secondary-color);
      border-bottom: 1px solid var(--light-gray);
      padding-bottom: 0.5rem;
      margin-bottom: 1rem;
    }
  }
  
  .statement-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--light-gray);
    
    &:last-child {
      border-bottom: none;
      font-weight: 600;
      color: var(--secondary-color);
    }
  }
  
  .positive {
    color: var(--success-color);
  }
  
  .negative {
    color: var(--danger-color);
  }
`;

const FinancialMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const MetricCard = styled.div`
  background: var(--light-gray);
  padding: 1rem;
  border-radius: var(--border-radius);
  text-align: center;
  
  .metric-label {
    font-size: 0.9rem;
    color: var(--dark-gray);
    margin-bottom: 0.5rem;
  }
  
  .metric-value {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--primary-color);
  }
  
  .metric-change {
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }
`;

const WorkingCapitalSection = styled.div`
  background: white;
  border-radius: var(--border-radius);
  padding: 1rem;
  margin-bottom: 1rem;
  
  .working-capital-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    margin-top: 1rem;
  }
  
  .wc-item {
    text-align: center;
    padding: 0.5rem;
    background: var(--light-gray);
    border-radius: var(--border-radius);
    
    .wc-label {
      font-size: 0.8rem;
      color: var(--dark-gray);
      margin-bottom: 0.25rem;
    }
    
    .wc-value {
      font-weight: 600;
      color: var(--secondary-color);
    }
  }
`;

const ProjectFinancialDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const [project, setProject] = useState<any>(null);
  const [financialSummary, setFinancialSummary] = useState<ProjectFinancialSummary | null>(null);
  const [payments, setPayments] = useState<ProjectPayment[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [cashFlow, setCashFlow] = useState<ProjectCashFlow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'payments' | 'expenses' | 'cashflow'>('overview');
  
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (projectId) {
        try {
          setLoading(true);
          setError(null);
          
          // Fetch project data
          const projectResponse = await apiService.getProject(projectId);
          
          if (projectResponse.success && projectResponse.data) {
            const projectData = projectResponse.data;
            setProject(projectData);

            // Fetch real payments for this project
            const paymentsResponse = await apiService.getPayments({ projectId });
            const realPayments = paymentsResponse.data || [];
            
            // Fetch real expenses for this project
            const expensesResponse = await apiService.getExpenses({ projectId });
            const realExpenses = expensesResponse.data || [];
            
            // Store expenses for the expenses tab
            setExpenses(realExpenses);
            
            // Calculate actual financial metrics from real data
            const budget = projectData.budget.total;
            const totalPayments = realPayments.reduce((sum: number, payment: any) => sum + payment.amount, 0);
            const totalExpenses = realExpenses.reduce((sum: number, expense: any) => sum + expense.amount, 0);
            const approvedPayments = realPayments.filter((payment: any) => payment.approved);
            const pendingPayments = realPayments.filter((payment: any) => !payment.approved);
            const approvedExpenses = realExpenses.filter((expense: any) => expense.status === 'Approved');
            const pendingExpenses = realExpenses.filter((expense: any) => expense.status === 'Recorded' || expense.status === 'Pending Approval');
            
            // Use project's totalIncome field instead of calculating from payments
            const income = projectData.totalIncome || 0; // Use project's totalIncome field
            const expenses = totalExpenses; // Real expenses from database
            const balance = income - expenses;
            const variance = budget - expenses;
            
            const summary: ProjectFinancialSummary = {
              projectId: projectData._id || projectData.id || '',
              projectName: projectData.name,
              budget: {
                total: budget,
                materials: projectData.budget?.materials || 0,
                labor: projectData.budget?.labor || 0,
                equipment: projectData.budget?.equipment || 0,
                subContracts: projectData.budget?.subContracts || 0,
                others: projectData.budget?.others || 0
              },
              actual: {
                income: income,
                expenses: expenses,
                balance: income - expenses
              },
              variance: {
                budgetVsActual: budget - (income - expenses),
                percentage: budget > 0 ? ((budget - (income - expenses)) / budget) * 100 : 0
              },
              currency: projectData.budget?.currency || 'TZS',
              lastUpdated: new Date()
            };
            
            setFinancialSummary(summary);
            
            // Convert real payments to ProjectPayment format
            const paymentRecords: ProjectPayment[] = realPayments.map((payment: any) => ({
              id: payment._id || payment.id,
              projectId: payment.projectId,
              projectName: payment.projectName,
              paymentType: payment.paymentType,
              amount: payment.amount,
              currency: payment.currency,
              description: payment.description,
              paymentDate: new Date(payment.paymentDate),
              paymentMethod: payment.paymentMethod,
              reference: payment.reference || '',
              attachments: payment.attachments || [],
              createdBy: payment.createdBy,
              createdAt: new Date(payment.createdAt),
              approved: payment.approved,
              approvedBy: payment.approvedBy,
              approvedAt: payment.approvedAt ? new Date(payment.approvedAt) : undefined
            }));
            
            setPayments(paymentRecords);
            
            // Calculate real cash flow data
            const cashFlowData: ProjectCashFlow = {
              projectId: projectData._id || projectData.id || '',
              projectName: projectData.name,
              budget,
              income,
              expenses,
              balance,
              currency: projectData.budget?.currency || 'TZS',
              lastUpdated: new Date(),
              operatingCashFlow: income - expenses,
              freeCashFlow: (income - expenses) * 0.8,
              cashFlowMargin: income > 0 ? ((income - expenses) / income) * 100 : 0,
              cashConversionCycle: 45,
              workingCapital: {
                currentAssets: income * 1.2,
                currentLiabilities: expenses * 0.8,
                workingCapital: (income * 1.2) - (expenses * 0.8),
                currentRatio: expenses > 0 ? (income * 1.2) / (expenses * 0.8) : 0,
                quickRatio: expenses > 0 ? (income * 0.8) / (expenses * 0.8) : 0,
                cashRatio: expenses > 0 ? income / (expenses * 0.8) : 0
              },
              cashFlowStatement: {
                netIncome: income - expenses,
                depreciation: budget * 0.05,
                workingCapitalChanges: -budget * 0.02,
                operatingCashFlow: (income - expenses) + (budget * 0.05) - (budget * 0.02),
                capitalExpenditures: -budget * 0.15,
                freeCashFlow: (income - expenses) + (budget * 0.05) - (budget * 0.02) - (budget * 0.15)
              }
            };
            
            setCashFlow(cashFlowData);
          } else {
            setError('Project not found. The project may have been deleted or you don\'t have permission to view it.');
          }
        } catch (error: any) {
          console.error("Error fetching project details:", error);
          setError(error.message || 'Failed to load project details. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProjectDetails();
  }, [projectId]);
  
  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const getPaymentTypeVariant = (type: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (type) {
      case 'Advance': return 'info';
      case 'Progress': return 'primary';
      case 'Final': return 'success';
      case 'Retention': return 'warning';
      default: return 'secondary';
    }
  };

  const getExpenseStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'danger';
      case 'Pending Approval': return 'warning';
      case 'Recorded': return 'info';
      default: return 'secondary';
    }
  };

  const getExpenseCategoryVariant = (category: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (category) {
      case 'Materials': return 'primary';
      case 'Labor': return 'success';
      case 'Equipment': return 'warning';
      case 'Transportation': return 'info';
      case 'Others': return 'secondary';
      default: return 'secondary';
    }
  };
  
  return (
    <div>
      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading project financial details...</p>
        </div>
      )}
      
      {error && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ color: 'var(--danger-color)', marginBottom: '1rem' }}>{error}</p>
          <Button as={Link} to="/accounting">
            Back to Accounting
          </Button>
        </div>
      )}
      
      {!loading && !error && project && financialSummary && (
        <>
          <DetailHeader>
            <div>
              <h1 className="page-title">{project.name} - Financial Details</h1>
              <p style={{ color: 'var(--dark-gray)', margin: '0.5rem 0 0 0' }}>
                Project ID: {project._id || project.id}
              </p>
            </div>
            <FlexContainer gap="0.5rem">
              <Button as={Link} to={`/accounting/payment/new?projectId=${project._id || project.id}`}>
                + Add Payment
              </Button>
              <OutlineButton as={Link} to="/accounting">
                Back to Accounting
              </OutlineButton>
            </FlexContainer>
          </DetailHeader>
      
          <SummaryGrid>
            <SummaryItem>
              <div className="label">Total Budget</div>
              <div className="value">{formatCurrency(financialSummary.budget.total, financialSummary.currency)}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Total Income</div>
              <div className="value positive">{formatCurrency(financialSummary.actual.income, financialSummary.currency)}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Total Expenses</div>
              <div className="value negative">{formatCurrency(financialSummary.actual.expenses, financialSummary.currency)}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Net Balance</div>
              <div className={`value ${financialSummary.actual.balance >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(financialSummary.actual.balance, financialSummary.currency)}
              </div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Payments Count</div>
              <div className="value">{payments.length}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Approved Payments</div>
              <div className="value positive">{payments.filter(p => p.approved).length}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Pending Payments</div>
              <div className="value warning">{payments.filter(p => !p.approved).length}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Expenses Count</div>
              <div className="value">{expenses.length}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Approved Expenses</div>
              <div className="value positive">{expenses.filter(e => e.status === 'Approved').length}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Pending Expenses</div>
              <div className="value warning">{expenses.filter(e => e.status === 'Recorded' || e.status === 'Pending Approval').length}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Budget Utilization</div>
              <div className="value">
                {financialSummary.budget.total > 0 ? 
                  ((financialSummary.actual.expenses / financialSummary.budget.total) * 100).toFixed(1) + '%' : 
                  '0%'
                }
              </div>
            </SummaryItem>
          </SummaryGrid>
      
          <Card>
            <CardHeader>
              <FlexContainer>
                <h3>Financial Overview</h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button 
                    onClick={() => setActiveTab('overview')}
                    style={{ 
                      background: activeTab === 'overview' ? 'var(--primary-color)' : 'var(--light-gray)',
                      color: activeTab === 'overview' ? 'white' : 'var(--dark-gray)'
                    }}
                  >
                    Overview
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('payments')}
                    style={{ 
                      background: activeTab === 'payments' ? 'var(--primary-color)' : 'var(--light-gray)',
                      color: activeTab === 'payments' ? 'white' : 'var(--dark-gray)'
                    }}
                  >
                    Payments
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('expenses')}
                    style={{ 
                      background: activeTab === 'expenses' ? 'var(--primary-color)' : 'var(--light-gray)',
                      color: activeTab === 'expenses' ? 'white' : 'var(--dark-gray)'
                    }}
                  >
                    Expenses
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('cashflow')}
                    style={{ 
                      background: activeTab === 'cashflow' ? 'var(--primary-color)' : 'var(--light-gray)',
                      color: activeTab === 'cashflow' ? 'white' : 'var(--dark-gray)'
                    }}
                  >
                    Cash Flow
                  </Button>
                </div>
              </FlexContainer>
            </CardHeader>
            <CardBody>
              {activeTab === 'overview' && (
                <div>
                  <DetailSection>
                    <SectionTitle>Budget Breakdown</SectionTitle>
                    <Grid columns="1fr 1fr" gap="1rem">
                      <DetailItem>
                        <DetailLabel>Materials</DetailLabel>
                        <DetailValue>{formatCurrency(financialSummary.budget.materials, financialSummary.currency)}</DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Labor</DetailLabel>
                        <DetailValue>{formatCurrency(financialSummary.budget.labor, financialSummary.currency)}</DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Equipment</DetailLabel>
                        <DetailValue>{formatCurrency(financialSummary.budget.equipment, financialSummary.currency)}</DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Sub-Contracts</DetailLabel>
                        <DetailValue>{formatCurrency(financialSummary.budget.subContracts, financialSummary.currency)}</DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Others</DetailLabel>
                        <DetailValue>{formatCurrency(financialSummary.budget.others, financialSummary.currency)}</DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Budget Variance</DetailLabel>
                        <DetailValue>
                          <VarianceIndicator variance={financialSummary.variance.budgetVsActual}>
                            {formatCurrency(financialSummary.variance.budgetVsActual, financialSummary.currency)}
                            <br />
                            <small>({financialSummary.variance.percentage.toFixed(1)}%)</small>
                          </VarianceIndicator>
                        </DetailValue>
                      </DetailItem>
                    </Grid>
                  </DetailSection>
                </div>
              )}
              
              {activeTab === 'payments' && (
                <div>
                  <DetailSection>
                    <FlexContainer justify="space-between" align="center">
                      <SectionTitle>Payment History</SectionTitle>
                      <Button as={Link} to={`/accounting/payment/new?projectId=${project.id}`}>
                        + Add Payment
                      </Button>
                    </FlexContainer>
                    {payments.length > 0 ? (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableHeader>Payment ID</TableHeader>
                              <TableHeader>Type</TableHeader>
                              <TableHeader>Amount</TableHeader>
                              <TableHeader>Date</TableHeader>
                              <TableHeader>Method</TableHeader>
                              <TableHeader>Status</TableHeader>
                              <TableHeader>Actions</TableHeader>
                            </TableRow>
                          </TableHead>
                          <tbody>
                            {payments.map((payment) => (
                              <TableRow key={payment.id}>
                                <TableCell>
                                  <Link to={`/accounting/payment/${payment.id}`}>{payment.id}</Link>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={getPaymentTypeVariant(payment.paymentType)}>
                                    {payment.paymentType}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <strong>{formatCurrency(payment.amount, payment.currency)}</strong>
                                </TableCell>
                                <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                                <TableCell>{payment.paymentMethod}</TableCell>
                                <TableCell>
                                  <Badge variant={payment.approved ? 'success' : 'warning'}>
                                    {payment.approved ? 'Approved' : 'Pending'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Link to={`/accounting/payment/${payment.id}`}>
                                    <OutlineButton>View</OutlineButton>
                                  </Link>
                                </TableCell>
                              </TableRow>
                            ))}
                          </tbody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <p>No payments recorded for this project.</p>
                    )}
                  </DetailSection>
                </div>
              )}
              
              {activeTab === 'expenses' && (
                <div>
                  <DetailSection>
                    <FlexContainer justify="space-between" align="center">
                      <SectionTitle>Expense History</SectionTitle>
                      <Button as={Link} to={`/accounting/expense/new?projectId=${project._id || project.id}`}>
                        + Add Expense
                      </Button>
                    </FlexContainer>
                    {expenses.length > 0 ? (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableHeader>Expense ID</TableHeader>
                              <TableHeader>Description</TableHeader>
                              <TableHeader>Category</TableHeader>
                              <TableHeader>Amount</TableHeader>
                              <TableHeader>Date</TableHeader>
                              <TableHeader>Status</TableHeader>
                              <TableHeader>Actions</TableHeader>
                            </TableRow>
                          </TableHead>
                          <tbody>
                            {expenses.map((expense) => (
                              <TableRow key={expense._id || expense.id}>
                                <TableCell>
                                  <Link to={`/accounting/expense/${expense._id || expense.id}`}>
                                    {expense._id || expense.id}
                                  </Link>
                                </TableCell>
                                <TableCell>
                                  <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {expense.description}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={getExpenseCategoryVariant(expense.category)}>
                                    {expense.category}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <strong>{formatCurrency(expense.amount, expense.currency)}</strong>
                                </TableCell>
                                <TableCell>{formatDate(new Date(expense.date))}</TableCell>
                                <TableCell>
                                  <Badge variant={getExpenseStatusVariant(expense.status)}>
                                    {expense.status}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Link to={`/accounting/expense/${expense._id || expense.id}`}>
                                    <OutlineButton>View</OutlineButton>
                                  </Link>
                                </TableCell>
                              </TableRow>
                            ))}
                          </tbody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <p>No expenses recorded for this project.</p>
                    )}
                  </DetailSection>
                </div>
              )}
              
              {activeTab === 'cashflow' && cashFlow && projectId && (
                <CashFlowAnalysis projectId={projectId} />
              )}
            </CardBody>
          </Card>
          
          <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
            Developed by Sandfish
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectFinancialDetail; 